import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getAuthUser } from 'store/auth/selectors';
import {
  CURRENT_RELEASE_FEATURE_DOT_IDS,
  DISMISSED_FEATURE_DOTS_MAX_SIZE,
} from 'shared/config/featureDotsConfig';
import {
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';

const DISMISSED_FEATURE_DOTS_PREF = 'dismissedFeatureDots';

function pruneDismissed(
  dismissed: string[],
  currentIds: readonly string[],
  maxSize: number
): string[] {
  const valid = dismissed.filter((id) => currentIds.includes(id));
  if (valid.length <= maxSize) return valid;
  return valid.slice(-maxSize);
}

/**
 * Feature-dots use the same user preferences pipeline as other app settings
 * (e.g. returns list/board/calendar view): stored in backend via
 * updateCurrentUserPreferencesRequest, read from currentUser.prefDetails.
 */

export function useFeatureDot(dotId: string): {
  showDot: boolean;
  dismiss: () => void;
} {
  const handlePreferences = usePreferences();
  const currentUser = useSelector(getAuthUser);
  const [dismissed, setDismissed] = useState<string[]>([]);

  const userId = currentUser?.userUuid ?? '';
  const currentIds = CURRENT_RELEASE_FEATURE_DOT_IDS as readonly string[];

  useEffect(() => {
    if (!userId) return;

    const raw = handlePreferences(
      PreferenceActions.GET,
      PreferenceSections.FEATURE_DOTS,
      { name: DISMISSED_FEATURE_DOTS_PREF },
      userId
    );
    const dismissedList = Array.isArray(raw) ? raw : [];
    const pruned = pruneDismissed(
      dismissedList,
      currentIds,
      DISMISSED_FEATURE_DOTS_MAX_SIZE
    );

    if (pruned.length < dismissedList.length) {
      handlePreferences(
        PreferenceActions.SET,
        PreferenceSections.FEATURE_DOTS,
        { name: DISMISSED_FEATURE_DOTS_PREF, value: pruned },
        userId
      );
    }

    setDismissed(pruned);
    // Re-run when user or backend preferences change (same as returns view mode)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentUser?.prefDetails?.[1]]);

  const showDot = useMemo(
    () => currentIds.includes(dotId) && !dismissed.includes(dotId),
    [dotId, dismissed]
  );

  const dismiss = useCallback(() => {
    if (!userId) return;
    setDismissed((prev) => (prev.includes(dotId) ? prev : [...prev, dotId]));

    const raw = handlePreferences(
      PreferenceActions.GET,
      PreferenceSections.FEATURE_DOTS,
      { name: DISMISSED_FEATURE_DOTS_PREF },
      userId
    );
    const dismissedList = Array.isArray(raw) ? raw : [];
    const pruned = pruneDismissed(
      dismissedList,
      currentIds,
      DISMISSED_FEATURE_DOTS_MAX_SIZE
    );
    if (pruned.includes(dotId)) return;

    const next = [...pruned, dotId].slice(-DISMISSED_FEATURE_DOTS_MAX_SIZE);
    handlePreferences(
      PreferenceActions.SET,
      PreferenceSections.FEATURE_DOTS,
      { name: DISMISSED_FEATURE_DOTS_PREF, value: next },
      userId
    );
  }, [userId, dotId]);

  return { showDot, dismiss };
}
